<template>
  <div class="campaign-tab-system pb-[40px] lg:pb-0">
    <!--========== Tab Navigation ==========-->
    <nav class="detail-nav bg-base">
      <div class="max-w-[1216px] mx-auto px-4 sm:px-6 md:px-8 lg:px-8 xl:px-0 border-b border-gray-500">
        <div class="relative">
          <!-- Tab List Container -->
          <div class="overflow-hidden smd:overflow-x-auto flex justify-between items-center">
            <!-- Tab Buttons -->
            <div
              class="flex transition-transform duration-300 ease-out smd:transform-none smd:gap-2 md:gap-4 lg:gap-6"
              :style="slideStyle"
            >
              <button
                v-for="tab in tabs"
                :key="tab.id"
                :class="getTabClasses(tab.id)"
                @click="switchToTab(tab.id)"
              >
                {{ tab.label }}
              </button>
            </div>

            <pledge-button class="hidden lg:flex max-h-10" />
          </div>

          <!-- Slide Controls (Mobile Only) -->
          <template v-if="showSlideControls">
            <button
              v-if="showPrevButton"
              @click="slidePrev"
              class="smd:hidden absolute top-0 left-0 z-10 flex justify-center items-center w-8 h-full cursor-pointer bg-gradient-to-r from-base to-transparent"
            >
              <i class="flex justify-center items-center w-6 h-6 rounded-full">
                <img src="../../../images/arrow-right.svg" alt="Previous" class="w-4 h-4 text-gray-150 transform rotate-180">
              </i>
            </button>

            <button
              v-if="showNextButton"
              @click="slideNext"
              class="smd:hidden absolute top-0 right-0 z-10 flex justify-center items-center w-8 h-full cursor-pointer bg-gradient-to-l from-base to-transparent"
            >
              <i class="flex justify-center items-center w-6 h-6 rounded-full">
                <img src="../../../images/arrow-right.svg" alt="Next" class="w-4 h-4 text-gray-150">
              </i>
            </button>
          </template>
        </div>
      </div>
    </nav>

    <!--========== Main Content  ==========-->
    <main class="tab-content min-h-[400px]">
      <div class="max-w-[1216px] mx-auto p-4 sm:px-6 md:px-8 xl:px-0">
        <div class="flex flex-col lg:flex-row gap-4 xl:gap-4">
          <!-- Dynamic Content Area -->
          <section class="flex-1 flex flex-col gap-y-4 sm:gap-y-6">
            <component
              :is="currentTabComponent"
              v-bind="getComponentProps(currentTab)"
              v-on="getComponentEvents(currentTab)"
            />
          </section>

          <!-- Sidebar Area -->
          <aside v-if="isBasicDataReady" class="campaign-detail-sidebar">
            <campaign-sidebar
              :campaign-data="campaignData"
              :rewards-data="rewardsData"
            />
          </aside>

          <!-- Sidebar Loading State -->
          <aside v-else class="campaign-sidebar space-y-6 lg:w-[298px]">
            <div class="bg-bg-base p-4 h-full rounded-xl">
              <div class="animate-pulse">
                <div class="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                <div class="h-8 bg-gray-300 rounded w-1/2 mb-4"></div>
                <div class="h-2 bg-gray-300 rounded w-full mb-4"></div>
                <div class="h-6 bg-gray-300 rounded w-1/3"></div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>

    <!--========== State Overlays  ==========-->
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="text-center">
        {{ $t('campaigns.show.loading') }}
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      {{ error }}
    </div>

    <mobile-pledge-bar v-if="isBasicDataReady" />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, markRaw, provide, readonly, getCurrentInstance } from 'vue'
import { useCampaignData } from '../../utils/useCampaignData'

// Helper function to get CSRF token
const getCsrfToken = () => {
  const metaTag = document.querySelector('meta[name="csrf-token"]')
  return metaTag ? metaTag.getAttribute('content') : ''
}

// Tab components are now configured in the CONFIG object within setup()

export default {
  props: {
    campaignId: {
      type: [String, Number],
      required: true
    },
    campaignMode: {
      type: String,
      default: 'show',
      validator: (value) => ['show', 'preview', 'prelaunch'].includes(value)
    }
  },
  setup(props) {
    //========== i18n 設定 ==========
    const instance = getCurrentInstance()
    const $t = instance?.proxy?.$t || (() => '')

    // 統一的翻譯函數
    const t = (key, options = {}) => {
      if (window.i18nextGlobal) {
        return window.i18nextGlobal(key, options)
      }
      // Fallback 機制
      return $t(key, options) || key
    }

    //========== 組件和樣式定義 ==========
    // 組件映射
    const componentMap = {
      campaign: () => import('./detail-sections/Campaign.vue'),
      news: () => import('./detail-sections/News.vue'),
      faq: () => import('./detail-sections/Faq.vue'),
      terms: () => import('./detail-sections/Terms.vue'),
      comments: () => import('./detail-sections/CommentsCollection.vue')
    }

    // Tab 樣式
    const baseTabClass = 'nav-tab flex-shrink-0 basis-[35%] smd:basis-auto transition-colors duration-200 whitespace-nowrap text-12px px-3 py-2 sm:text-14px sm:px-4 sm:py-3 smd:text-16px smd:px-6 smd:py-4'
    const activeTabClass = 'active font-bold border-b-4 border-orange-300 text-white'
    const inactiveTabClass = 'hover:border-b-4 hover:border-orange-300 border-b-4 border-transparent text-gray-150'

    // 滑動相關常數
    const maxVisibleTabs = 3
    const slideStep = 2
    const slideTransformPercent = 33.33

    // Tab 配置
    const preLaunchTabs = computed(() => [
      { id: 'campaign', label: t('campaigns.show.tabs.campaign') },
      { id: 'faq', label: t('campaigns.show.tabs.faq') },
      { id: 'terms', label: t('campaigns.show.tabs.terms') }
    ])
    const fullLaunchTabs = computed(() => [
      { id: 'campaign', label: t('campaigns.show.tabs.campaign') },
      { id: 'faq', label: t('campaigns.show.tabs.faq') },
      { id: 'news', label: t('campaigns.show.tabs.news') },
      { id: 'comments', label: t('campaigns.show.tabs.comments') },
      { id: 'terms', label: t('campaigns.show.tabs.terms') }
    ])

    //========== 1. 數據層：Campaign Data Management ==========
    const {
      fetchCampaignData,
      fetchRewards,
      fetchNews,
      campaignData,
      rewardsData,
      isBasicDataReady,
      isAnyLoading,
      // News 管理功能
      newsViewMode,
      selectedNewsUpdate,
      newsUpdates,
      parseNewsHash,
      findUpdateById,
      setNewsDetailMode,
      setNewsListMode,
      updateNewsURLHash,
      // 獎勵管理功能
      updateRewardQuantity,
      clearRewardQuantities,
      rewardQuantities,
      totalQuantity,
      // 工具函數
      utils
    } = useCampaignData(props.campaignId, props.campaignMode)

    //========== 購物車數量同步 ==========
    // 進入頁面時，將 cart 中屬於當前 campaign 的 reward 數量帶回 sidebar
    const hydrateRewardQuantitiesFromCart = async () => {
      if (props.campaignMode !== 'show') return

      try {
        const response = await fetch('/api/v1/campaign_cart/current', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })

        if (!response.ok) return // 401 (未登入) 或其他錯誤靜默略過

        const payload = await response.json()
        const items = payload?.cart?.items || []

        clearRewardQuantities()
        items.forEach((item) => {
          const itemCampaignId = item.campaign?.id ?? item.main_source_id
          const rewardId = item.reward?.id
          if (rewardId && String(itemCampaignId) === String(props.campaignId)) {
            updateRewardQuantity(rewardId, item.quantity)
          }
        })
      } catch (_err) {
        // 網路錯誤或解析失敗：保留 sidebar 預設數量為 0
      }
    }

    //========== 2. 狀態層：Component State ==========
    const currentTab = ref('campaign')
    const isLoading = ref(false)
    const error = ref(null)
    const loadedComponents = ref({})

    // 滑動相關狀態
    const slidePosition = ref(0)

    // Pledge 相關狀態
    const isPledgeLoading = ref(false)

    //========== 3. 計算層：UI 狀態 ==========
    const isPreviewMode = computed(() => {
      return window.location.pathname.includes('/preview')
    })

    const isPreLaunchCampaign = computed(() => {
      return campaignData.value?.is_pre_launch === true
    })

    // 直接的狀態控制
    const shouldShowCountdown = computed(() => isPreLaunchCampaign.value && !isPreviewMode.value)
    const shouldShowFullTabs = computed(() => !isPreLaunchCampaign.value || isPreviewMode.value)

    // 檢查 campaign 是否已結束
    const isCampaignEnded = computed(() => {
      if (!campaignData.value?.end_at) return false
      return new Date(campaignData.value.end_at) < new Date()
    })

    const shouldDisableControls = computed(() => {
      return isPreviewMode.value || isPreLaunchCampaign.value || isCampaignEnded.value
    })

    const canPledge = computed(() => {
      return !isCampaignEnded.value && !isPreLaunchCampaign.value
    })

    // Tab Configuration
    const tabs = computed(() => {
      return shouldShowFullTabs.value ? fullLaunchTabs.value : preLaunchTabs.value
    })

    // Component Loading
    const currentTabComponent = computed(() => {
      return loadedComponents.value[currentTab.value] || null
    })

    // Slide Controls
    const showSlideControls = computed(() => tabs.value.length > maxVisibleTabs)
    const showPrevButton = computed(() => slidePosition.value > 0)
    const showNextButton = computed(() => {
      const maxPosition = Math.max(0, tabs.value.length - maxVisibleTabs)
      return slidePosition.value < maxPosition
    })
    const slideStyle = computed(() => ({
      transform: `translateX(-${slidePosition.value * slideTransformPercent}%)`
    }))

    //========== 4. 方法層：Business Logic ==========
    // Props Management Methods
    const getComponentProps = (tabName) => {
      switch (tabName) {
        case 'campaign':
          // Campaign.vue 使用 inject，不需要 props
          return {}

        case 'news':
          return {
            campaignId: props.campaignId,
            campaignMode: props.campaignMode,
            utils,
            viewMode: newsViewMode.value,
            selectedUpdate: selectedNewsUpdate.value
          }

        case 'faq':
        case 'terms':
          return {
            campaignId: props.campaignId,
            campaignMode: props.campaignMode,
            utils
          }

        case 'comments':
          return {
            campaignId: props.campaignId,
            campaignMode: props.campaignMode,
            commentableType: 'Campaign',
            commentableId: props.campaignId
          }

        default:
          return {}
      }
    }

    // Component Loading Methods
    const loadComponent = async (tabName) => {
      if (loadedComponents.value[tabName]) {
        return loadedComponents.value[tabName]
      }

      try {
        isLoading.value = true
        const componentLoader = componentMap[tabName]
        if (componentLoader) {
          const component = await componentLoader()
          loadedComponents.value[tabName] = markRaw(component.default)
          return markRaw(component.default)
        }
      } catch (err) {
        error.value = `Failed to load ${tabName} component: ${err.message}`
        console.error('Component loading error:', err)
      } finally {
        isLoading.value = false
      }
    }

    // Tab Management Methods
    const getTabClasses = (tabId) => {
      const isActive = currentTab.value === tabId
      return `${baseTabClass} ${isActive ? activeTabClass : inactiveTabClass}`
    }

    const switchToTab = async (tabName) => {
      if (currentTab.value === tabName) return

      error.value = null
      await loadComponent(tabName)
      currentTab.value = tabName

      // Only set the simple tab name in the hash if the hash is not a more specific format
      const currentHash = window.location.hash.replace('#', '')
      if (!currentHash.startsWith(tabName + '/')) {
        window.location.hash = tabName
      }
    }

    // Slide Methods
    const slidePrev = () => {
      if (slidePosition.value > 0) {
        slidePosition.value = Math.max(0, slidePosition.value - slideStep)
      }
    }

    const slideNext = () => {
      const maxPosition = Math.max(0, tabs.value.length - maxVisibleTabs)
      if (slidePosition.value < maxPosition) {
        slidePosition.value = Math.min(maxPosition, slidePosition.value + slideStep)
      }
    }

    //========== 5. 事件層：Event Handlers ==========
    // Event Management Methods
    const getComponentEvents = (tabName) => {
      const baseEvents = {
        loading: handleLoading,
        error: handleError
      }

      switch (tabName) {
        case 'news':
          return {
            ...baseEvents,
            'view-detail': handleNewsViewDetail,
            'back-to-list': handleNewsBackToList
          }

        case 'campaign':
        case 'faq':
        case 'terms':
        case 'comments':
        default:
          return baseEvents
      }
    }

    const handleLoading = (loading) => {
      isLoading.value = loading
    }

    const handleError = (errorMsg) => {
      error.value = errorMsg
    }

    //========== 6. News 事件處理區塊 ==========
    const handleNewsViewDetail = (update) => {
      setNewsDetailMode(update)
      updateNewsURLHash(update.id)
    }

    const handleNewsBackToList = () => {
      setNewsListMode()
      updateNewsURLHash()
    }

    //========== 6.5. Pledge 處理 ==========
    const handlePledge = async () => {
      if (isPledgeLoading.value || totalQuantity.value === 0) {
        return
      }

      try {
        isPledgeLoading.value = true

        // 包含所有 reward (含 quantity = 0)，後端 set_quantity 才能依據 sidebar 完整覆寫 cart
        const rewardQuantitiesPayload = (rewardsData.value || []).reduce((acc, reward) => {
          acc[reward.id] = rewardQuantities.value[reward.id] || 0
          return acc
        }, {})

        const response = await fetch('/campaigns/add_to_cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken()
          },
          body: JSON.stringify({
            id: props.campaignId,
            reward_quantities: rewardQuantitiesPayload
          })
        })

        if (response.ok) {
          // 成功後跳轉到購物車頁面
          window.location.href = '/campaigns/cart'
        } else {
          throw new Error('Failed to add to cart')
        }
      } catch (err) {
        error.value = '添加到購物車失敗，請稍後重試'
        console.error('Pledge error:', err)
      } finally {
        isPledgeLoading.value = false
      }
    }

    //========== 7. URL 管理區塊 ==========
    const initFromURL = async () => {
      const hash = window.location.hash.replace('#', '')
      const validTabs = shouldShowFullTabs.value
        ? ['campaign', 'news', 'faq', 'comments', 'terms']
        : ['campaign', 'terms']

      // 處理 news 詳細頁面
      if (hash.startsWith('news/') && shouldShowFullTabs.value) {
        const newsState = parseNewsHash(hash)

        if (newsState.mode === 'detail' && newsState.updateId) {
          // If newsUpdates have not been loaded yet, load the data first
          if (!newsUpdates.value || newsUpdates.value.length === 0) {
            await fetchNews()
          }

          const update = findUpdateById(newsUpdates.value, newsState.updateId)
          if (update) {
            setNewsDetailMode(update)
          } else {
            // Fallback: If not found, return to list mode
            setNewsListMode()
            window.location.hash = 'news'
          }
        } else {
          // Set to list mode when not in detail mode
          setNewsListMode()
        }
        await switchToTab('news')
        return
      }

      // 處理一般 tab 切換
      const initialTab = validTabs.includes(hash) ? hash : 'campaign'
      setNewsListMode()
      setTimeout(() => switchToTab(initialTab), 0)

      if (!window.location.hash || !validTabs.includes(hash)) {
        window.location.hash = initialTab
      }
    }

    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || 'campaign'

      // 處理 news 詳細頁面
      if (hash.startsWith('news/')) {
        const newsState = parseNewsHash(hash)

        if (currentTab.value !== 'news') {
          switchToTab('news')
        }

        newsViewMode.value = newsState.mode
        if (newsState.mode === 'detail' && newsState.updateId) {
          const update = findUpdateById(newsUpdates.value, newsState.updateId)
          if (update) {
            setNewsDetailMode(update)
          }
        }
        return
      }

      // 處理一般 tab 切換
      if (hash === 'news') {
        setNewsListMode()
      }

      if (hash !== currentTab.value) {
        switchToTab(hash)
      }
    }


    //========== 8. 生命週期區塊 ==========
    onMounted(async () => {
      try {
        await Promise.all([fetchCampaignData(), fetchRewards()])
        await hydrateRewardQuantitiesFromCart()
        await loadComponent('campaign')
        await initFromURL()
        window.addEventListener('hashchange', handleHashChange)
      } catch (err) {
        error.value = `Failed to load campaign data: ${err.message}`
        console.error('Campaign data loading error:', err)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('hashchange', handleHashChange)
    })

    //========== 9. Provide 區塊 ==========

    provide('campaignData', readonly(campaignData))
    provide('rewardsData', readonly(rewardsData))
    provide('isLoading', readonly(isAnyLoading))
    provide('updateRewardQuantity', updateRewardQuantity)
    provide('rewardQuantities', readonly(rewardQuantities))
    provide('totalQuantity', readonly(totalQuantity))
    provide('isPledgeLoading', readonly(isPledgeLoading))
    provide('handlePledge', handlePledge)

    // 提供狀態控制變數
    provide('shouldShowCountdown', readonly(shouldShowCountdown))
    provide('shouldDisableControls', readonly(shouldDisableControls))
    provide('isCampaignEnded', readonly(isCampaignEnded))
    provide('isPreLaunchCampaign', readonly(isPreLaunchCampaign))
    provide('canPledge', readonly(canPledge))

    // 提供統一的翻譯函數
    provide('t', t)


    //========== 11. Return 區塊 ==========
    return {
      // === Tab 相關 ===
      currentTab,
      currentTabComponent,
      tabs,
      switchToTab,
      getTabClasses,

      // === Props 和事件管理 ===
      getComponentProps,
      getComponentEvents,

      // === 滑動相關 ===
      slidePosition,
      slideStyle,
      showSlideControls,
      showPrevButton,
      showNextButton,
      slidePrev,
      slideNext,

      // === 數據相關 ===
      campaignData,
      rewardsData,
      isBasicDataReady,

      // === 狀態相關 ===
      isLoading,
      error,
      handleLoading,
      handleError,

      // === UI 狀態相關 ===
      isPreviewMode,
      shouldShowCountdown,
      shouldShowFullTabs,

      // === News 相關 ===
      newsViewMode,
      selectedNewsUpdate,
      newsUpdates,
      handleNewsViewDetail,
      handleNewsBackToList,

      // === 工具 ===
      utils
    }
  }
}
</script>